// Public — Customer review submission page. Reached via the unique link
// sent after Gelato flags the order as delivered. The token in the URL
// is the sole authorisation.
// i18n : dictionnaire inline COPY (FR/EN/ES/PT).
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import Layout from "@/components/Layout";
import { apiFetch } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const COPY = {
  fr: {
    err_invalid_link: "Lien invalide", err_no_rating: "Merci de donner une note.", err_send: "Impossible d'envoyer votre avis.",
    invalid_h: "Lien invalide", invalid_p: "Ce lien de dépôt d'avis n'est plus valable.", back_to_shop: "Retour à la boutique",
    thanks_h: "Merci de votre avis !", thanks_p: "Votre message est reçu — il sera publié après une brève relecture éditoriale.", back_to_card: "Retour à la carte",
    eyebrow: "Votre avis", h1: "Comment était votre carte ?", sub_end: "— 30 secondes pour aider les prochains dégustateurs.",
    label_rating: "Votre note", rating_aria: "Note",
    label_title: "Résumé (optionnel)", ph_title: "Ex. : Superbe qualité d'impression",
    label_comment: "Votre avis", ph_comment: "Racontez votre expérience : le rendu, la livraison, l'usage…",
    label_name: "Signature (facultatif)", ph_name: "Prénom N.", name_hint: "Nom affiché publiquement. Laisser vide = « Client geoVINUM ».",
    submit: "Envoyer mon avis", submitting: "Envoi…",
  },
  en: {
    err_invalid_link: "Invalid link", err_no_rating: "Please give a rating.", err_send: "Unable to submit your review.",
    invalid_h: "Invalid link", invalid_p: "This review link is no longer valid.", back_to_shop: "Back to shop",
    thanks_h: "Thanks for your review!", thanks_p: "Your message has been received — it will be published after a brief editorial review.", back_to_card: "Back to the map",
    eyebrow: "Your review", h1: "How was your map?", sub_end: "— 30 seconds to help future tasters.",
    label_rating: "Your rating", rating_aria: "Rating",
    label_title: "Summary (optional)", ph_title: "e.g. Superb print quality",
    label_comment: "Your review", ph_comment: "Tell us about your experience: the render, delivery, use…",
    label_name: "Signature (optional)", ph_name: "First name L.", name_hint: "Publicly displayed name. Leave blank = \u201cgeoVINUM Customer\u201d.",
    submit: "Send my review", submitting: "Sending…",
  },
  es: {
    err_invalid_link: "Enlace no válido", err_no_rating: "Por favor, deja una nota.", err_send: "No se pudo enviar tu opinión.",
    invalid_h: "Enlace no válido", invalid_p: "Este enlace de opinión ya no es válido.", back_to_shop: "Volver a la tienda",
    thanks_h: "¡Gracias por tu opinión!", thanks_p: "Tu mensaje se ha recibido — se publicará tras una breve revisión editorial.", back_to_card: "Volver al mapa",
    eyebrow: "Tu opinión", h1: "¿Cómo estuvo tu mapa?", sub_end: "— 30 segundos para ayudar a los próximos catadores.",
    label_rating: "Tu nota", rating_aria: "Nota",
    label_title: "Resumen (opcional)", ph_title: "Ej.: Excelente calidad de impresión",
    label_comment: "Tu opinión", ph_comment: "Cuéntanos tu experiencia: la impresión, la entrega, el uso…",
    label_name: "Firma (opcional)", ph_name: "Nombre A.", name_hint: "Nombre mostrado públicamente. Dejar vacío = \u00abCliente geoVINUM\u00bb.",
    submit: "Enviar mi opinión", submitting: "Enviando…",
  },
  pt: {
    err_invalid_link: "Link inválido", err_no_rating: "Por favor, deixe uma nota.", err_send: "Não foi possível enviar a sua avaliação.",
    invalid_h: "Link inválido", invalid_p: "Este link de avaliação já não é válido.", back_to_shop: "Voltar à loja",
    thanks_h: "Obrigado pela sua avaliação!", thanks_p: "A sua mensagem foi recebida — será publicada após uma breve revisão editorial.", back_to_card: "Voltar ao mapa",
    eyebrow: "A sua avaliação", h1: "Como foi o seu mapa?", sub_end: "— 30 segundos para ajudar os próximos apreciadores.",
    label_rating: "A sua nota", rating_aria: "Nota",
    label_title: "Resumo (opcional)", ph_title: "Ex.: Excelente qualidade de impressão",
    label_comment: "A sua avaliação", ph_comment: "Conte-nos a sua experiência: a impressão, a entrega, o uso…",
    label_name: "Assinatura (opcional)", ph_name: "Nome A.", name_hint: "Nome mostrado publicamente. Deixar vazio = \u00abCliente geoVINUM\u00bb.",
    submit: "Enviar avaliação", submitting: "A enviar…",
  },
};

const ReviewSubmitPage = () => {
  const { token } = useParams();
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data, error: err } = await apiFetch(`/api/shop/reviews/token/${token}`);
      if (cancelled) return;
      if (!ok) setError(err || c.err_invalid_link);
      else setContext(data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token, c.err_invalid_link]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { setError(c.err_no_rating); return; }
    setSubmitting(true); setError(null);
    const { ok, error: err } = await apiFetch(`/api/shop/reviews/${token}`, {
      method: "POST",
      body: JSON.stringify({ rating, title, comment, display_name: displayName }),
    });
    if (!ok) setError(err || c.err_send);
    else setDone(true);
    setSubmitting(false);
  };

  if (loading) {
    return <Layout testId="page-review-submit-loading"><div className="min-h-[70vh] flex items-center justify-center"><Loader2 size={24} className="animate-spin text-[color:var(--gv-pink)]" /></div></Layout>;
  }

  if (error && !context) {
    return (
      <Layout testId="page-review-submit-error">
        <div className="max-w-lg mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)] mb-3">{c.invalid_h}</h1>
          <p className="text-[14px] text-[color:var(--gv-ink)]/65 mb-6">{c.invalid_p}</p>
          <Link to="/shop" className="btn-primary inline-flex">{c.back_to_shop} <ArrowLeft size={14} className="rotate-180" /></Link>
        </div>
      </Layout>
    );
  }

  if (context?.already_submitted || done) {
    return (
      <Layout testId="page-review-submit-thanks">
        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <CheckCircle2 size={44} className="mx-auto text-[color:var(--gv-pink)] mb-4" />
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)] mb-3">{c.thanks_h}</h1>
          <p className="text-[14px] text-[color:var(--gv-ink)]/70 mb-6">
            {c.thanks_p}
          </p>
          <Link to={`/shop/${context?.product_id || ""}`} className="btn-primary inline-flex" data-testid="back-to-product">
            {c.back_to_card} <ArrowLeft size={14} className="rotate-180" />
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout testId="page-review-submit">
      <div className="max-w-xl mx-auto px-6 py-20">
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-2">{c.eyebrow}</div>
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-2">
          {c.h1}
        </h1>
        <p className="text-[14px] text-[color:var(--gv-ink)]/70 mb-8">
          <strong>{context?.product_title}</strong> {c.sub_end}
        </p>

        <form onSubmit={submit} className="space-y-6 bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2 block">{c.label_rating}</label>
            <div className="flex gap-1.5" role="radiogroup" aria-label={c.rating_aria}>
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hover || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={rating === n}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`rating-star-${n}`}
                  >
                    <Star size={30} className={filled ? "fill-[color:var(--gv-pink)] text-[color:var(--gv-pink)]" : "text-[color:var(--gv-ink)]/25"} />
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2 block">{c.label_title}</label>
            <input type="text" maxLength={140} value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder={c.ph_title}
                   className="gv-input w-full" data-testid="review-title" />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2 block">{c.label_comment}</label>
            <textarea maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)}
                      rows={5}
                      placeholder={c.ph_comment}
                      className="gv-input w-full" data-testid="review-comment" />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2 block">{c.label_name}</label>
            <input type="text" maxLength={80} value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                   placeholder={c.ph_name}
                   className="gv-input w-full" data-testid="review-display-name" />
            <div className="text-[11px] text-[color:var(--gv-ink)]/50 mt-1">{c.name_hint}</div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px] text-[13px]">{error}</div>}

          <button type="submit" disabled={submitting || !rating}
                  className="btn-primary w-full justify-center" data-testid="review-submit">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> {c.submitting}</> : c.submit}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default ReviewSubmitPage;
